import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { ButtonGroup, Popper, Grow, Paper, ClickAwayListener, MenuList } from '@material-ui/core';
import { ChevronDown } from '../../icons';
import { useDevice } from '../../providers/Device';
import { useBiAction } from '../BiTracking/hooks';
import { BottomSheet } from '../BottomSheet';
import { Button } from '../Button';
import { Row } from '../Layout';
import { MenuItem } from '../MenuItem';
import { useSplitButtonStyles } from './SplitButton.styles';
import { getTestId, SPLIT_BUTTON_TOGGLE_BI_NAME } from './SplitButton.utils';
export function SplitButton(props) {
    var _ref;
    const { label, children, placement, modifiers, popperClassName, flushBottom = false, mobile, bi, noBi } = props, rest = _object_without_properties(props, [
        "label",
        "children",
        "placement",
        "modifiers",
        "popperClassName",
        "flushBottom",
        "mobile",
        "bi",
        "noBi"
    ]);
    const { isMobileDevice } = useDevice();
    const [isPopperOpen, setIsPopperOpen] = useState(false);
    // eslint-disable-next-line no-restricted-syntax -- WHY: BottomSheet IS the declarative overlay the rule points at, and it takes `open` as a prop, so this seagull surface owns that state itself instead of deferring to showModal.
    const [isOpen, setIsOpen] = useState(false);
    const anchorRef = React.useRef(null);
    const testId = getTestId(props.testId);
    const overrides = useSplitButtonStyles({
        flushBottom
    });
    const logBiAction = useBiAction();
    const toggleBiConfig = (_ref = bi === null || bi === void 0 ? void 0 : bi.toggleButton) !== null && _ref !== void 0 ? _ref : {
        name: SPLIT_BUTTON_TOGGLE_BI_NAME
    };
    const closeMobileSheet = ()=>setIsOpen(false);
    const buildMobileSheetChildren = ()=>React.Children.map(children, (child)=>/*#__PURE__*/ React.isValidElement(child) ? /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({}, child.props), {
                onClick: ()=>{
                    var _child_props_onClick, _child_props;
                    closeMobileSheet();
                    (_child_props_onClick = (_child_props = child.props).onClick) === null || _child_props_onClick === void 0 ? void 0 : _child_props_onClick.call(_child_props);
                }
            })) : child);
    if (isMobileDevice && mobile != null) {
        var _mobile_triggerBi, _rest_arialabel;
        // WHY: the collapsed trigger is the toggle's counterpart, not the main action — gating it on
        // `intent` would hide menu items the role IS allowed to reach; each sheet item gates itself.
        const { intent } = rest, triggerProps = _object_without_properties(rest, [
            "intent"
        ]);
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({}, triggerProps, noBi ? {
                    noBi
                } : {
                    bi: (_mobile_triggerBi = mobile.triggerBi) !== null && _mobile_triggerBi !== void 0 ? _mobile_triggerBi : toggleBiConfig
                }), {
                    size: "iconOnly",
                    testId: testId,
                    "aria-label": (_rest_arialabel = rest['aria-label']) !== null && _rest_arialabel !== void 0 ? _rest_arialabel : label,
                    onClick: ()=>setIsOpen(true),
                    children: mobile.icon
                })),
                /*#__PURE__*/ _jsxs(BottomSheet, {
                    open: isOpen,
                    onClose: closeMobileSheet,
                    children: [
                        mobile.mainActionInSheet !== false && /*#__PURE__*/ _jsx(MenuItem, {
                            testId: "".concat(testId, "-main"),
                            intent: intent,
                            disabled: rest.disabled || rest.loading,
                            onClick: ()=>{
                                var _rest_onClick;
                                closeMobileSheet();
                                if (!noBi) {
                                    logBiAction({
                                        entityType: 'button',
                                        entityName: bi.mainButton.name,
                                        actionType: 'click',
                                        data: _object_spread_props(_object_spread({}, bi.mainButton.data), {
                                            test_id: "".concat(testId, "-main")
                                        })
                                    });
                                }
                                (_rest_onClick = rest.onClick) === null || _rest_onClick === void 0 ? void 0 : _rest_onClick.call(rest);
                            },
                            children: /*#__PURE__*/ _jsxs(Row, {
                                gap: 12,
                                alignItems: "center",
                                children: [
                                    mobile.icon,
                                    /*#__PURE__*/ _jsx("span", {
                                        children: label
                                    })
                                ]
                            })
                        }),
                        buildMobileSheetChildren()
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(ButtonGroup, {
                "data-testid": testId,
                variant: "contained",
                ref: anchorRef,
                className: overrides.buttonGroup,
                children: [
                    /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({}, rest, noBi ? {
                        noBi
                    } : {
                        bi: bi.mainButton
                    }), {
                        testId: "".concat(testId, "-main"),
                        children: label
                    })),
                    /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({}, noBi ? {
                        noBi
                    } : {
                        bi: toggleBiConfig
                    }), {
                        disabled: rest.disabled,
                        size: rest.size,
                        className: overrides.toggleBtn,
                        onClick: isMobileDevice ? ()=>setIsOpen(true) : ()=>setIsPopperOpen((prev)=>!prev),
                        testId: "".concat(testId, "-arrow"),
                        children: /*#__PURE__*/ _jsx(ChevronDown, {})
                    }))
                ]
            }),
            isMobileDevice && /*#__PURE__*/ _jsx(BottomSheet, {
                open: isOpen,
                onClose: closeMobileSheet,
                children: buildMobileSheetChildren()
            }),
            !isMobileDevice && /*#__PURE__*/ _jsx(Popper, {
                open: isPopperOpen,
                anchorEl: anchorRef.current,
                transition: true,
                className: popperClassName || overrides.popperZIndex,
                placement: placement || 'bottom-end',
                modifiers: modifiers,
                children: (param)=>{
                    let { TransitionProps, placement } = param;
                    return /*#__PURE__*/ _jsx(Grow, _object_spread_props(_object_spread({}, TransitionProps), {
                        style: {
                            transformOrigin: placement.includes('bottom') ? 'center top' : 'center bottom'
                        },
                        children: /*#__PURE__*/ _jsx(Paper, {
                            className: overrides.paper,
                            children: /*#__PURE__*/ _jsx(ClickAwayListener, {
                                onClickAway: ()=>setIsPopperOpen(false),
                                children: /*#__PURE__*/ _jsx(MenuList, {
                                    className: overrides.menuList,
                                    onClick: ()=>setIsPopperOpen(false),
                                    children: children
                                })
                            })
                        })
                    }));
                }
            })
        ]
    });
}
